import * as React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TriangleAlert, Bug, Droplets, ShieldCheck, Sprout, Wind } from "lucide-react";
const environmentalFactors = [{
  id: "factor-1",
  title: "Moisture-Driven Termite Swarms",
  description: "Arcadia's high humidity, frequent afternoon downpours, and rich soil create the perfect incubator for subterranean termites. Off-the-shelf DIY sprays only eliminate the visible workers foraging above ground. Meanwhile, the queen and millions of colony members remain completely untouched beneath your foundation, continuing to compromise your home's structural integrity.",
  icon: Droplets
}, {
  id: "factor-2",
  title: "Agricultural Drainage & Mosquitoes",
  description: "The extensive network of agricultural drainage ditches, retention ponds, and irrigation canals surrounding Arcadia provides virtually endless breeding grounds for mosquitoes. Store-bought foggers and citronella candles offer only fleeting hours of relief against these resilient, locally adapted populations that constantly migrate from nearby water sources.",
  icon: Wind
}, {
  id: "factor-3",
  title: "Barn-Originated Fly Infestations",
  description: "Proximity to livestock areas, equestrian facilities, and agricultural processing means Arcadia homes face severe, relentless fly pressure. These aren't standard house flies; they are aggressive agricultural pests. Standard fly swatters or sticky traps are entirely ineffective against populations that breed this rapidly and carry numerous pathogens.",
  icon: Bug
}];
export default function ArcadiaPestPressureAnalysis() {
  return <section id="arcadiapestpressureanalysis" className="bg-background">
      {}
      <div className="bg-foreground text-primary-foreground py-20 px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          
          {}
          <div className="mb-16 max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <TriangleAlert className="text-background size-6" />
              <span className="text-background font-default font-bold tracking-wider uppercase text-sm">
                Arcadia Environmental Analysis
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold leading-tight mb-6">
              The Arcadia Pest Reality: <br />
              <span className="text-background">Why DIY Fails</span>
            </h2>
            <p className="text-primary-foreground/80 font-serif text-lg md:text-xl leading-relaxed">
              Living in Florida's agricultural heartland brings unique challenges. Discover why consumer-grade products are no match for Arcadia's relentless pest pressure.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            
            {}
            <div className="lg:col-span-5 space-y-6">
              {environmentalFactors.map((factor, i) => <Card key={factor.id} data-index={i} className="bg-card text-card-foreground border-none shadow-xl relative overflow-hidden group">
                  <div className="absolute top-0 left-0 w-1 h-full bg-background transition-all duration-300 group-hover:w-2" />
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-4 mb-2">
                      <div className="p-3 bg-foreground text-primary-foreground">
                        <factor.icon className="size-6" />
                      </div>
                      <CardTitle className="font-default text-xl md:text-2xl">
                        {factor.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="font-serif text-muted-foreground leading-relaxed">
                      {factor.description}
                    </p>
                  </CardContent>
                </Card>)}
            </div>

            {}
            <div className="lg:col-span-7 space-y-8">
              <div className="bg-primary/10 border-l-4 border-background p-6 md:p-8">
                <h3 className="text-2xl md:text-3xl font-default font-bold mb-6 text-background">
                  The true Cost of Ineffective Control
                </h3>
                
                <div className="space-y-6 font-serif text-primary-foreground/90 text-lg leading-relaxed">
                  <p>
                    Living in Arcadia means embracing the beauty and heritage of Florida's heartland, but it also necessitates confronting some of the most aggressive and persistent pest populations in the state. The unique combination of rich agricultural soil, dense natural vegetation, and persistent moisture creates an environment where pests don't just survive; they thrive and multiply at alarming rates.
                  </p>
                  
                  <p>
                    Many well-intentioned homeowners attempt to tackle these issues with off-the-shelf products from local hardware stores, only to find themselves trapped in a frustrating and costly cycle of temporary fixes and recurring infestations. The harsh reality is that DIY pest control in Arcadia is often a losing battle against nature's relentless pressure. When you rely on consumer-grade sprays and baits, you are typically only addressing the superficial symptoms of a pest problem, completely missing the root cause hidden within your walls or beneath your soil.
                  </p>

                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_property_pest_inspection.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_property_pest_inspection.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/arcadia_property_pest_inspection.png-wvc-sizes" alt="Detailed inspection of an Arcadia property foundation for pest vulnerabilities" className="w-full h-64 object-cover my-8 border-2 border-background/20 grayscale hover:grayscale-0 transition-all duration-500" loading="lazy" />

                  <p>
                    Consider the moisture-driven termite swarms that plague Arcadia properties. These require deep-trenching techniques and specialized, highly regulated termiticides that are simply not available to the general public. A DIY approach might kill a few visible swarmers on your windowsill, but it leaves the structural integrity of your home entirely at the mercy of the massive colony operating silently below. Similarly, the barn-originated fly infestations common in areas bordering livestock facilities cannot be managed with standard household solutions. Professional intervention involves targeted baiting, environmental management, and residual treatments that break the breeding cycle entirely.
                  </p>

                  <p>
                    At Peace River Pest Control, we understand the specific environmental factors driving pest activity in Arcadia. As an FDACS Licensed Pest Control and Termite Operator, our technicians don't just spray indiscriminately; we conduct comprehensive environmental assessments. We identify the agricultural drainage issues contributing to your mosquito problems and implement Integrated Pest Management (IPM) strategies that provide long-term, cost-effective relief.
                  </p>

                  <p>
                    Investing in professional pest control is not just about immediate comfort; it's about protecting your most valuable asset. The cumulative cost of repeatedly purchasing ineffective DIY products, combined with the potential for severe structural damage from termites or health risks from mosquitoes and flies, far outweighs the investment in a reliable, professional service. Stop wasting time and money on solutions that don't work in Arcadia's unique environment. Choose the local experts who guarantee results.
                  </p>
                </div>
              </div>

              {}
              <div className="flex flex-col sm:flex-row items-center gap-6 pt-8 border-t border-primary-foreground/20">
                <Button asChild size="lg" className="w-full sm:w-auto bg-background text-foreground hover:bg-background/90 font-default text-lg h-14 px-8">
                  <Link to="/ContactPage">
                    Schedule Free Arcadia Inspection
                  </Link>
                </Button>
                <div className="flex items-center gap-3 text-primary-foreground/80 font-serif">
                  <ShieldCheck className="size-8 text-background" />
                  <div className="flex flex-col">
                    <span className="font-bold text-primary-foreground">Fully Insured & Licensed</span>
                    <span className="text-sm">FDACS Certified Operator</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>;
}